import { useCallback, useContext, useMemo } from 'react'

import { useSelector } from 'react-redux'
import { isUndefined } from 'lodash'

import { getKeyConfig, getLaneConfig, getLaneKey, getQuotasMatrix, getQuotasPeriods, getSeed, getStorageKey, INCLUSION_LINE_KEY, LaneTypes, quotaEvent, sortLanes } from '../../../Components/shared/Quotas/utils'
import SchoolQuotaDisplayer from '../../institution/Quotas/SchoolQuotasView/SchoolQuotaDisplayer'
import SectionTooltip from '../../institution/Quotas/SchoolQuotasView/SectionTooltip'
import DragTable from '../../../Components/antd/Tables/DragTable/DragTable'
import { QuotaFormModalContext, Types } from '../../../Providers/Forms/QuotaForm/QuotaFormModalProvider'
import { colorShade, getLuminanceFromColorCode } from '../../../utils/colors'
import { areQuotaEquivalent } from '../../../utils/entities/quota'
import { ColorsPalette } from '../../../config/colors'
import useQuota from '../../../utils/api/hooks/useQuota'
import { Actions, dataUpsert, deleteData, Events } from '../../../utils/events'
import { Axes } from '../../../Components/antd/Tables/DragTable/ResizingDiv'
import { firstOrDefault, getIdOrNull, isNonEmptyArray } from '../../../utils'
import LOCAL_STORAGE_KEYS from '../../../utils/localStorage'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { includeSection } from '../../../utils/entities/sectionGroup'

const DEFAULT_COLOR = {
  border: ColorsPalette.GREY,
  background: colorShade(ColorsPalette.FOREST_GREEN, 30),
  text: getLuminanceFromColorCode(ColorsPalette.FOREST_GREEN)
}

const PlacesTable = ({
  config, holidayPeriods, period, quotasMap, seed, selectedData = [], studentPathways, sectionGroups,
  onChange, onSelectData
}) => {
  const { setFormHandler } = useContext(QuotaFormModalContext)
  const t = useSelector(selectTranslate)

  const periods = useMemo(() => getQuotasPeriods(period, t), [t, period])
  const storageKey = useMemo(() => getStorageKey(seed, LOCAL_STORAGE_KEYS.PLACES_TABLE.QUOTAS_MATRIX), [seed])

  const lines = useMemo(() => {
    const { SECTION, GROUP } = LaneTypes

    const linesConfig = quotasMap.places.reduce((acc, quota) => {
      const { type, id, name } = getLaneConfig(quota)

      const key = id === INCLUSION_LINE_KEY ? id : getLaneKey(type, id)

      if (!acc[key]) {
        acc[key] = { id: key, places: 1, name: name, allowedYears: [], infos: [] }
      }

      return acc
    }, { [INCLUSION_LINE_KEY]: { id: -1, places: 1, name: '*', allowedYears: [], infos: [] } })

    if (studentPathways.length) {
      studentPathways.forEach(sp => {
        const section = sp.sectionToYear.section.id
        const group = sectionGroups.find(group => includeSection(group, section))

        const laneKey = group !== undefined
          ? getLaneKey(GROUP, getIdOrNull(group))
          : getLaneKey(SECTION, section)

        const key = linesConfig[laneKey] ? laneKey : INCLUSION_LINE_KEY

        linesConfig[key].allowedYears.push(sp.sectionToYear)
      })
    }

    const lanes = Object.values(linesConfig)
    const inclusions = lanes.shift()

    return [inclusions].concat(sortLanes(lanes))
  }, [quotasMap.places, quotasMap.quotasNull, studentPathways, sectionGroups])

  const data = useMemo(() => getQuotasMatrix(
    lines,
    periods.weeks,
    [...quotasMap.desideratas, ...quotasMap.quotas],
    quota => ({ style: DEFAULT_COLOR, disabled: !quota.isDesiderata, draggableAxes: [Axes.HORIZONTAL, Axes.VERTICAL], resizableAxes: Object.values(Axes) }),
    quota => <SchoolQuotaDisplayer quota={quota} overlay={config.overlay} isDesiderata />,
    storageKey
  ), [quotasMap.quotas, periods.weeks, lines, config.overlay, storageKey])

  const quotaHandler = useQuota({ callback: onChange })

  const holidayWeeks = useMemo(() => {
    if (holidayPeriods.length === 0) return []

    const holidayWeeksIndexes = periods.weeks.reduce((acc, week, index) => {
      if (holidayPeriods.some(holiday => holiday.includes(week))) {
        acc.push(index)
      }

      return acc
    }, [])

    return holidayWeeksIndexes
  }, [holidayPeriods, periods.weeks])

  const handleAction = useCallback((event, quota) => {
    if (event === Events.DRAG_STOP) {
      dataUpsert(quota, firstOrDefault(selectedData), quotaHandler.upsert, areQuotaEquivalent)
    } else if (event === Events.STRICT_VERTICAL_DRAG_STOP) {
      onChange(quota, Actions.UPDATE)
    } else if (event === Events.DOUBLE_CLICK || event === Events.LONG_CLICK) {
      setFormHandler({ data: quota, callback: onChange, type: Types.DESIDERATA })
    } else if (event === Events.DELETE_PRESSED) {
      deleteData(selectedData, quotaHandler.delete, onChange)
    }
  }, [selectedData, onChange, setFormHandler, quotaHandler.delete, quotaHandler.upsert])

  const handleEvent = useCallback((rawEvent, rawData, key) => {
    const quotaSeed = getSeed(key, rawData)
    const { quota, event } = quotaEvent(data.data[key] ?? [], data.matrix[key] ?? [], periods, rawEvent, rawData, { ...quotaSeed, ...seed }, storageKey)

    handleAction(event, quota)
  }, [periods, seed, handleAction, storageKey])

  const handleSelectData = useCallback(({ values }) => onSelectData(values), [onSelectData])

  const linesWithTooltips = useMemo(() => lines.map(l => {
    const laneType = l.id === INCLUSION_LINE_KEY ? undefined : getKeyConfig(l.id).type

    if (isNonEmptyArray(l.allowedYears) && !isUndefined(laneType)) {
      l.tooltip = <SectionTooltip sectionToYears={l.allowedYears} type={laneType} />
    }

    return l
  }), [lines])

  return (
    <DragTable
      columns={periods.months}
      lines={linesWithTooltips}
      data={data.data}
      overflows={data.overflows}
      config={config}
      highlightedColumns={holidayWeeks}
      selectedData={selectedData}
      onSelectData={handleSelectData}
      shouldDisplayOverflow={false}
      title={t('common.sections')}
      onEvent={handleEvent}
    />
  )
}

export default PlacesTable
